import {
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import { API_URL } from "const/constants";
import useKothar from "context/useKothar";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { ensureProviderAccount, findProviderByUniversityId } from "./providerUtils";

const PROVIDER_SEND_TOOLTIP = "Needs verification and import.";

const isTermProviderSendEligible = (term) =>
  term?.providerSendEligible === true ||
  (term?.providerSendEligible == null && term?.imported === true);

export default function SendToProviderModal({
  open,
  onClose,
  accountMasterId,
  groups,
  accountDetails,
  onSent,
}) {
  const [{ providerList }, { refetchProvider }] = useKothar();
  const [agents, setAgents] = useState([]);
  const [agentName, setAgentName] = useState("");
  const [selectedDetailIds, setSelectedDetailIds] = useState([]);
  const [termMode, setTermMode] = useState("remaining"); // "remaining" | "specific"
  const [allTerms, setAllTerms] = useState([]);
  const [selectedTermIds, setSelectedTermIds] = useState([]);
  const [loadingTerms, setLoadingTerms] = useState(false);
  const [sending, setSending] = useState(false);

  // Only admission entries that are saved
  const admissionDetails = useMemo(
    () => (accountDetails || []).filter((d) => d.id && d.module === "Admission"),
    [accountDetails]
  );

  const detailLabel = (detail) => {
    if (!detail) return "Unknown entry";
    const admission = detail.admissionValues || {};
    return (
      [admission.university, admission.course].filter(Boolean).join(" - ") || `Entry #${detail.id}`
    );
  };

  // Derive provider from the selected entries' university
  const derivedProvider = useMemo(() => {
    if (!selectedDetailIds.length) return null;
    const detail = admissionDetails.find((d) => selectedDetailIds.includes(d.id));
    const uniId = detail?.admissionValues?.universityId;
    if (!uniId) return null;
    return findProviderByUniversityId(providerList, uniId) || null;
  }, [selectedDetailIds, admissionDetails, providerList]);

  const eligibleTermIds = useMemo(
    () => allTerms.filter(isTermProviderSendEligible).map((t) => t.id),
    [allTerms]
  );

  // Auto-select if only one admission entry
  useEffect(() => {
    if (open && admissionDetails.length === 1) {
      setSelectedDetailIds([admissionDetails[0].id]);
    }
  }, [open, admissionDetails]);

  useEffect(() => {
    if (!open) {
      setAgents([]);
      setAgentName("");
      setSelectedDetailIds([]);
      setAllTerms([]);
      setSelectedTermIds([]);
      setTermMode("remaining");
    }
  }, [open]);

  useEffect(() => {
    const uniId = derivedProvider
      ? Number(derivedProvider.universityId ?? derivedProvider.university?.id)
      : null;
    if (Number.isFinite(uniId) && uniId > 0) {
      loadAgents(uniId);
    } else {
      setAgents([]);
      setAgentName("");
    }
  }, [derivedProvider]);

  useEffect(() => {
    if (termMode === "specific" && selectedDetailIds.length > 0) {
      loadTermsForSelectedClients();
    } else {
      setAllTerms([]);
      setSelectedTermIds([]);
    }
  }, [termMode, selectedDetailIds]);

  const loadAgents = async (uniId) => {
    try {
      const res = await axios.get(`${API_URL}/universities/${uniId}/agents`);
      const list = res?.data?.data || [];
      setAgents(list);
      if (list.length > 0) setAgentName(list[0].name);
    } catch {
      setAgents([]);
    }
  };

  const loadTermsForSelectedClients = async () => {
    setLoadingTerms(true);
    const collected = [];
    for (const detailId of selectedDetailIds) {
      try {
        const res = await axios.get(
          `${API_URL}/admission-terms/admission-detail/${detailId}/unsent`
        );
        const detail = admissionDetails.find((d) => d.id === detailId);
        (res?.data?.data || []).forEach((t) =>
          collected.push({
            ...t,
            detailId,
            detailLabel: detailLabel(detail) || `Entry #${detailId}`,
          })
        );
      } catch {}
    }
    setAllTerms(collected);
    setSelectedTermIds(collected.filter(isTermProviderSendEligible).map((t) => t.id));
    setLoadingTerms(false);
  };

  const toggleDetail = (id) =>
    setSelectedDetailIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const toggleTerm = (id) => {
    const term = allTerms.find((t) => t.id === id);
    if (!term || !isTermProviderSendEligible(term)) return;
    setSelectedTermIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSend = async () => {
    if (!selectedDetailIds.length) {
      toast.warning("Select at least one admission entry");
      return;
    }
    if (!derivedProvider) {
      toast.warning("No provider found for the selected admission's university");
      return;
    }

    if (termMode === "specific") {
      const sendIds = selectedTermIds.filter((id) => eligibleTermIds.includes(id));
      if (!sendIds.length) {
        toast.warning("Select at least one verified and imported term to send.");
        return;
      }
    } else if (eligibleTermIds.length === 0 && allTerms.length > 0) {
      toast.warning("No verified and imported terms are available to send.");
      return;
    }

    setSending(true);
    try {
      const accountProvider = await ensureProviderAccount(derivedProvider, refetchProvider);
      const sendTermIds =
        termMode === "specific"
          ? selectedTermIds.filter((id) => eligibleTermIds.includes(id))
          : null;
      await axios.post(`${API_URL}/provider-admissions/send`, {
        accountProviderId: accountProvider.accountProviderId,
        clientAccountDetailIds: selectedDetailIds,
        admissionTermIds: sendTermIds,
        agentName: agentName || null,
      });
      toast.success("Sent to provider successfully");
      onSent?.();
      onClose();
    } catch (e) {
      toast.error(
        e?.response?.data?.errorMessage ||
          e?.response?.data?.message ||
          e?.message ||
          "Failed to send to provider"
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send to Provider</DialogTitle>
      <DialogContent>
        {/* Admission entry selection */}
        <div className="mb-4 mt-1">
          <p className="text-sm font-medium mb-2">Select admission entries to send:</p>
          {admissionDetails.length === 0 ? (
            <p className="text-xs text-app-text-muted border rounded p-2">
              No saved admission entries found.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1 max-h-40 overflow-y-auto border rounded p-2">
              {admissionDetails.map((d) => (
                <Tooltip key={d.id} title={detailLabel(d)} arrow>
                  <Chip
                    label={detailLabel(d)}
                    size="small"
                    clickable
                    color={selectedDetailIds.includes(d.id) ? "primary" : "default"}
                    onClick={() => toggleDetail(d.id)}
                    sx={{ fontSize: 11 }}
                  />
                </Tooltip>
              ))}
            </div>
          )}
        </div>

        {/* Derived provider info */}
        {selectedDetailIds.length > 0 && (
          <div className="mb-4 p-2 bg-blue-50 border border-blue-200 rounded text-sm">
            {derivedProvider ? (
              <span>
                Provider:{" "}
                <strong>
                  {derivedProvider.universityName || derivedProvider.university?.name || "Unknown"}
                </strong>
              </span>
            ) : (
              <span className="text-red-600">
                No provider linked to this admission's university.
              </span>
            )}
          </div>
        )}

        {/* Agent Name */}
        {derivedProvider && (
          <div className="mb-4">
            <Autocomplete
              size="small"
              freeSolo
              options={agents}
              getOptionLabel={(o) => (typeof o === "string" ? o : o?.name ?? "")}
              value={agentName}
              onChange={(_, val) => setAgentName(typeof val === "string" ? val : val?.name ?? "")}
              onInputChange={(_, val) => setAgentName(val)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={agents.length ? "Agent Name (pre-filled)" : "Agent Name"}
                  size="small"
                />
              )}
            />
          </div>
        )}

        {/* Term selection mode */}
        {selectedDetailIds.length > 0 && (
          <div className="mb-3">
            <p className="text-sm font-medium mb-1">Terms to send:</p>
            <div className="flex gap-3">
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={termMode === "remaining"}
                    onChange={() => setTermMode("remaining")}
                  />
                }
                label={<span className="text-sm">All remaining unsent terms</span>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={termMode === "specific"}
                    onChange={() => setTermMode("specific")}
                  />
                }
                label={<span className="text-sm">Specific terms</span>}
              />
            </div>
            {termMode === "remaining" && (
              <p className="text-xs text-app-text-secondary mt-1">
                Only verified and imported terms are sent; pending terms are skipped.
              </p>
            )}

            {termMode === "specific" && (
              <div className="mt-2 border rounded p-2 max-h-36 overflow-y-auto">
                {loadingTerms ? (
                  <CircularProgress size={16} />
                ) : allTerms.length === 0 ? (
                  <p className="text-xs text-app-text-muted">
                    No unsent terms found for selected entries.
                  </p>
                ) : (
                  allTerms.map((t) => {
                    const eligible = isTermProviderSendEligible(t);
                    const label = (
                      <span className={`text-xs ${eligible ? "" : "text-app-text-muted"}`}>
                        {t.detailLabel} — Term {t.termNumber}
                        {t.year
                          ? ` (${t.month ?? ""}${t.month && t.year ? "/" : ""}${t.year})`
                          : ""}
                      </span>
                    );
                    const row = (
                      <FormControlLabel
                        key={t.id}
                        control={
                          <Checkbox
                            size="small"
                            checked={selectedTermIds.includes(t.id)}
                            disabled={!eligible}
                            onChange={() => toggleTerm(t.id)}
                          />
                        }
                        label={label}
                        sx={{ display: "flex", alignItems: "flex-start", ml: 0 }}
                      />
                    );
                    return eligible ? (
                      row
                    ) : (
                      <Tooltip key={t.id} title={PROVIDER_SEND_TOOLTIP} arrow placement="left">
                        <span className="block w-full">{row}</span>
                      </Tooltip>
                    );
                  })
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={sending || !selectedDetailIds.length || !derivedProvider}
        >
          {sending ? "Sending..." : "Send to Provider"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
